/**
 * Phase 2 Block C — Support & Contact page.
 *
 * Public route (no auth required) so the App-Store reviewer can reach
 * support without registering. Email auto-fills from AuthContext when
 * a user happens to be signed in, but is fully editable.
 *
 * Backend endpoint: POST /api/support/contact
 *   - Honeypot field `website` (hidden) for spam protection.
 *   - Rate-limit feedback via 429 response.
 *   - Sends an email to info@dealalert.ai via Resend.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";

const CATEGORY_VALUES = ["problem", "bug", "feature", "general"];

const MAX_MESSAGE = 5000;
const MAX_SUBJECT = 200;
const MIN_MESSAGE = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SupportPage() {
  const { t } = useTranslation("support");
  const { user } = useAuth();

  const [category, setCategory] = useState("problem");
  const [name, setName] = useState(() => user?.display_name || "");
  const [email, setEmail] = useState(() => user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Note: AuthContext reads `user` synchronously from localStorage on
  // first render, so the lazy initializers above already capture the
  // logged-in user's email. We deliberately do NOT add a useEffect
  // hydration backfill — it conflicts with React's
  // `set-state-in-effect` rule and the late-hydration case is rare
  // enough that the user can simply type their email.

  const messageCount = message.length;
  const remaining = useMemo(() => MAX_MESSAGE - messageCount, [messageCount]);

  const validate = () => {
    if (!category) return t("validate.category");
    if (!email || !EMAIL_RE.test(email)) return t("validate.email");
    if (message.trim().length < MIN_MESSAGE)
      return t("validate.minMessage", { min: MIN_MESSAGE });
    if (message.length > MAX_MESSAGE)
      return t("validate.maxMessage", { max: MAX_MESSAGE });
    if (subject.length > MAX_SUBJECT)
      return t("validate.maxSubject", { max: MAX_SUBJECT });
    if (name.length > 200) return t("validate.maxName");
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/support/contact", {
        category,
        name: name.trim() || null,
        email: email.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
        website: website || null,
      });
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError(t("rateLimited"));
      } else {
        setError(formatApiError(err) || t("sendFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mx-auto max-w-2xl py-10"
      data-testid="support-page"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-zinc-100">{t("title")}</h1>
        <p className="mt-2 text-sm text-zinc-400">{t("intro")}</p>
      </div>

      {success ? (
        <div
          className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center"
          data-testid="support-success"
        >
          <div className="text-4xl text-emerald-400">✓</div>
          <h2 className="mt-3 text-xl font-semibold text-zinc-100">
            {t("successTitle")}
          </h2>
          <p className="mt-2 text-sm text-zinc-400">
            {t("successBodyA")}{" "}
            <span className="text-zinc-200">{email}</span>.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              data-testid="support-send-another"
              onClick={() => {
                setSuccess(false);
                setSubject("");
                setMessage("");
                setError(null);
              }}
              className="rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-900"
            >
              {t("sendAnother")}
            </button>
            <Link
              to="/"
              data-testid="support-back-home"
              className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
            >
              {t("backHome")}
            </Link>
          </div>
        </div>
      ) : (
        <form
          className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-5"
          onSubmit={onSubmit}
          noValidate
          data-testid="support-form"
        >
          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wider text-zinc-500">
              {t("fields.category")} <span className="text-rose-400">*</span>
            </legend>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {CATEGORY_VALUES.map((value) => {
                const active = category === value;
                return (
                  <label
                    key={value}
                    data-testid={`support-category-${value}`}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                      active
                        ? "border-emerald-500/60 bg-emerald-500/10 text-zinc-100"
                        : "border-zinc-800 bg-zinc-950/50 text-zinc-400 hover:border-zinc-700"
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={value}
                      checked={active}
                      onChange={() => setCategory(value)}
                      className="sr-only"
                    />
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        active ? "bg-emerald-400" : "bg-zinc-700"
                      }`}
                    />
                    {t(`categories.${value}`)}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("fields.name")} htmlFor="support-name" optional optionalLabel={t("fields.optional")}>
              <input
                id="support-name"
                data-testid="support-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                autoComplete="name"
                className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500/60 focus:outline-none"
                placeholder={t("placeholders.name")}
              />
            </Field>
            <Field label={t("fields.email")} htmlFor="support-email" required>
              <input
                id="support-email"
                data-testid="support-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500/60 focus:outline-none"
                placeholder={t("placeholders.email")}
              />
            </Field>
          </div>

          <Field label={t("fields.subject")} htmlFor="support-subject" optional optionalLabel={t("fields.optional")}>
            <input
              id="support-subject"
              data-testid="support-subject-input"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={MAX_SUBJECT}
              className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500/60 focus:outline-none"
              placeholder={t("placeholders.subject")}
            />
          </Field>

          <Field
            label={t("fields.message")}
            htmlFor="support-message"
            required
            hint={`${messageCount} / ${MAX_MESSAGE} ${t("fields.chars")}`}
          >
            <textarea
              id="support-message"
              data-testid="support-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={7}
              maxLength={MAX_MESSAGE}
              required
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm leading-relaxed text-zinc-100 placeholder-zinc-500 focus:border-emerald-500/60 focus:outline-none"
              placeholder={t("placeholders.message")}
            />
            {remaining < 200 && (
              <p
                className={`mt-1.5 text-xs ${
                  remaining < 0 ? "text-rose-400" : "text-zinc-500"
                }`}
              >
                {t("fields.remaining", { count: remaining })}
              </p>
            )}
          </Field>

          {/* Honeypot — visually hidden, accessible-text hidden, never
              tab-reachable. Real users never see / fill this. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: 1,
              height: 1,
              overflow: "hidden",
            }}
          >
            <label htmlFor="support-website">
              Website (bitte leer lassen)
              <input
                id="support-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          {error && (
            <div
              data-testid="support-error"
              className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2.5 text-sm text-rose-200"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse items-stretch gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-zinc-500">
              {t("mailtoPrefix")}{" "}
              <a
                href="mailto:info@dealalert.ai"
                data-testid="support-mailto-link"
                className="text-zinc-300 underline underline-offset-4 hover:text-zinc-100"
              >
                info@dealalert.ai
              </a>
            </p>
            <button
              type="submit"
              data-testid="support-submit-btn"
              disabled={submitting}
              className="rounded-md bg-emerald-500 px-5 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t("sending") : t("submit")}
            </button>
          </div>
        </form>
      )}

      {/* Footer legal links — duplicate the global footer so the
          page is self-contained for an Apple reviewer who is reading
          it standalone. */}
      <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
        <Link
          to="/impressum"
          data-testid="support-legal-impressum"
          className="hover:text-zinc-300 underline-offset-4 hover:underline"
        >
          Impressum
        </Link>
        <Link
          to="/datenschutz"
          data-testid="support-legal-datenschutz"
          className="hover:text-zinc-300 underline-offset-4 hover:underline"
        >
          Datenschutz
        </Link>
        <Link
          to="/agb"
          data-testid="support-legal-agb"
          className="hover:text-zinc-300 underline-offset-4 hover:underline"
        >
          AGB
        </Link>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, required, optional, optionalLabel, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label
          htmlFor={htmlFor}
          className="text-xs font-medium uppercase tracking-wider text-zinc-500"
        >
          {label}{" "}
          {required && <span className="text-rose-400">*</span>}
          {optional && <span className="ml-1 text-[10px] text-zinc-600">{optionalLabel || "optional"}</span>}
        </label>
        {hint && <span className="text-[10px] text-zinc-600">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
